import type { User } from '../../../../types';
import { getShowDeleteUserDialog, getUsersToDelete } from '../../../Drivers.selectors';
import { DeleteUserDialog } from '../components/DeleteUserDialog';
import { setUsersToDelete, toggleDeleteUserDialog } from '../../drivers/sidebar/tabs/user/userSlice';
import { useAppDispatch, useAppSelector } from '../../../../configuration/setup/hooks';
import { useDeleteUserMutation } from '../../services/usersApi';
import { showErrorNotification, showSuccessNotification } from '../../notifications/notifications';
import { deleteUserSuccess } from '../../drivers/sidebar/sidebarSlice';
import { reportErrorToSentry } from '../../../../configuration/setup/sentry';
import { isApiErrorWithCode } from '../../services/apiErrors';

export interface DeleteUserDialogPropsFromState {
    usersToDelete: User[];
    showUsersDeleteDialog: boolean;
}

export interface DeleteUserDialogPropsFromDispatch {
    deleteUsers?: (users: User[]) => void;
    deleteUser?: (user: User) => void;
    toggleDeleteUserDialog: (show: boolean) => void;
    setUsersToDelete: (users: User[]) => void;
}

export const DeleteUserDialogContainer = () => {
    const dispatch = useAppDispatch();
    const usersToDelete = useAppSelector(getUsersToDelete);
    const showUsersDeleteDialog = useAppSelector(getShowDeleteUserDialog);
    const [deleteUserMutation] = useDeleteUserMutation();

    const deleteUsers = async (users: User[]) => {
        for (const user of users) {
            try {
                await deleteUserMutation(user).unwrap();
                showSuccessNotification('intl-msg:users.delete.success');
                dispatch(deleteUserSuccess());
            } catch (error) {
                if (isApiErrorWithCode(error) && (error.status === 401 || error.status === 403)) {
                    showErrorNotification('intl-msg:error.user.delete.unauthorized', true);
                } else {
                    reportErrorToSentry(
                        new Error(
                            `Could not delete user ${JSON.stringify(user)} due to the following error: ${JSON.stringify(error)}`
                        )
                    );
                    showErrorNotification('intl-msg:error.user.delete.internal', true);
                }
            }
        }
    };

    return (
        <DeleteUserDialog
            usersToDelete={usersToDelete}
            showUsersDeleteDialog={showUsersDeleteDialog}
            deleteUsers={deleteUsers}
            toggleDeleteUserDialog={show => {
                dispatch(toggleDeleteUserDialog(show));
            }}
            setUsersToDelete={users => {
                dispatch(setUsersToDelete(users));
            }}
        />
    );
};
